import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import BEM from '../../bem';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import TCClient from '../../../client';
import { PBXDirectoryEntry } from '../../../types';
import Modal from '../Modal';

const classes = BEM.with('CreateOrEditPBXDirectoryEntryModal');

type CreateOrEditPBXDirectoryEntryModalProps = {
  options: {
    entry?: PBXDirectoryEntry;
    onSuccess?: () => void;
  };
};

type MobxProps = {
  closeModal: () => void;
  currentOrganizationId: string;
  isOpen: boolean;
};

type FieldKey = 'displayName' | 'phoneNumber' | 'location' | 'department' | 'title';

type Field = {
  label: string;
  fieldKey: FieldKey;
  isRequired: boolean;
  errorMessage?: string;
  placeholder: string;
};

const FIELDS: Field[] = [
  {
    label: 'Display Name',
    fieldKey: 'displayName',
    isRequired: true,
    errorMessage: 'Display Name is required.',
    placeholder: 'Enter display name',
  },
  {
    label: 'Phone Number',
    fieldKey: 'phoneNumber',
    isRequired: true,
    errorMessage: 'Phone Number is required.',
    placeholder: 'Enter phone number',
  },
  {
    label: 'Location',
    fieldKey: 'location',
    isRequired: false,
    placeholder: 'Enter location',
  },
  {
    label: 'Department',
    fieldKey: 'department',
    isRequired: false,
    placeholder: 'Enter department',
  },
  {
    label: 'Title',
    fieldKey: 'title',
    isRequired: false,
    placeholder: 'Enter title',
  },
];

type FormFields = { [key in FieldKey]: string };

const DEFAULT_VALUES: FormFields = {
  displayName: '',
  phoneNumber: '',
  location: '',
  department: '',
  title: '',
};

const CreateOrEditPBXDirectoryEntryModal = ({
  closeModal,
  currentOrganizationId,
  isOpen,
  options: { entry, onSuccess },
}: MobxProps & CreateOrEditPBXDirectoryEntryModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const isEditing = Boolean(entry?.id);

  useEffect(() => {
    if (isOpen) {
      if (entry) {
        reset({
          displayName: entry.displayName || '',
          phoneNumber: entry.phoneNumber || '',
          location: entry.location || '',
          department: entry.department || '',
          title: entry.title || '',
        });
      } else {
        reset(DEFAULT_VALUES);
      }
      setApiError('');
    }
  }, [isOpen, entry, reset]);

  const onSubmit = async (formData: FormFields) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setApiError('');

    try {
      if (isEditing && entry?.id) {
        await TCClient.pbx.updatePbxDirectoryEntry({
          orgToken: currentOrganizationId,
          id: entry.id,
          entry: {
            ...formData,
            organizationId: entry.organizationId,
          },
        });
      } else {
        await TCClient.pbx.createPbxDirectoryEntry({
          orgToken: currentOrganizationId,
          entry: formData,
        });
      }
      onSuccess?.();
      closeModal();
    } catch (err) {
      setApiError('Failed to save directory entry. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      bodyClass={classes('body')}
      closeClass={classes('close-btn')}
      footerClass={classes('footer')}
      header={isEditing ? 'Edit Directory Entry' : 'Create Directory Entry'}
      headerClass={classes('header')}
      isOpen={isOpen}
      size={'large'}
      className={classes()}
      onRequestClose={closeModal}
      footerPrimaryActions={
        <button
          aria-label="Save"
          className={classes('primary-btn')}
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      }
      footerSecondaryActions={
        <button
          aria-label="Cancel"
          className={classes('secondary-btn')}
          type="button"
          onClick={closeModal}
        >
          Cancel
        </button>
      }
    >
      <div className={classes('form-container')}>
        {FIELDS.map(({ label, fieldKey, isRequired, errorMessage, placeholder }) => (
          <div className={classes('form-field')} key={fieldKey}>
            <label className={classes('label')}>
              {label}
              {isRequired ? ' *' : ''}
            </label>
            <Controller
              name={fieldKey}
              control={control}
              rules={{
                required: isRequired ? errorMessage : false,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <input
                  className={classes('input', { error: Boolean(errors[fieldKey]) })}
                  type="text"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={placeholder}
                />
              )}
            />
            {errors[fieldKey] && (
              <span className={classes('error-message')}>{errors[fieldKey]?.message}</span>
            )}
          </div>
        ))}
        {apiError && <div className={classes('error-message')}>{apiError}</div>}
      </div>
    </Modal>
  );
};

export default mobxInjectSelect<CreateOrEditPBXDirectoryEntryModalProps, MobxProps>({
  messengerStore: ['currentOrganizationId'],
})(CreateOrEditPBXDirectoryEntryModal);
